import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { resolveIllustration } from '../../common/aiIllustration';

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(2),
        maxWidth: '100%',
        overflowX: 'auto',
    },
    title: {
        marginBottom: theme.spacing(2),
        fontWeight: 600,
    },
    component: {
        marginBottom: theme.spacing(1.5),
    },
    textComponent: {
        fontSize: '1rem',
        lineHeight: 1.6,
    },
    latexComponent: {
        margin: theme.spacing(2, 0),
        '& svg': {
            maxWidth: '100%',
            height: 'auto',
        },
    },
    latexBlock: {
        padding: theme.spacing(1.5),
        backgroundColor: '#f9f9f9',
        borderRadius: '4px',
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    svgComponent: {
        display: 'flex',
        justifyContent: 'center',
        margin: theme.spacing(2, 0),
        '& svg': {
            width: '100%',
            height: 'auto',
        },
    },
    imageComponent: {
        display: 'flex',
        justifyContent: 'center',
        margin: theme.spacing(2, 0),
        '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '4px',
        },
    },
}));

const LatexComponent = ({ content, className }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (containerRef.current && window.latexToImg) {
            try {
                const svgElement = window.latexToImg(content);
                if (svgElement) {
                    svgElement.style.height = 'auto';
                    svgElement.style.width = '100%';
                    containerRef.current.innerHTML = '';
                    containerRef.current.appendChild(svgElement);
                }
            } catch (error) {
                console.error('Error rendering LaTeX:', error);
                containerRef.current.textContent = `{ ${content} }`;
            }
        }
    }, [content]);

    return <Box ref={containerRef} className={className} />;
};

const InlineLatexComponent = ({ content }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (containerRef.current && window.latexToImg) {
            try {
                const svgElement = window.latexToImg(content);
                if (svgElement) {
                    svgElement.style.display = 'inline-block';
                    svgElement.style.verticalAlign = 'middle';
                    svgElement.style.margin = '0 2px';
                    svgElement.style.maxHeight = '1.5em';
                    svgElement.style.height = 'auto';
                    svgElement.style.width = 'auto';
                    containerRef.current.innerHTML = '';
                    containerRef.current.appendChild(svgElement);
                }
            } catch (error) {
                console.error('Error rendering inline LaTeX:', error);
                containerRef.current.textContent = `$${content}$`;
            }
        }
    }, [content]);

    return <span ref={containerRef} style={{ display: 'inline-block', verticalAlign: 'middle' }} />;
};

// An "image" component carries an image search query, not a URL. The picture is fetched
// lazily, only once this renders, because each attempt costs a search or a paid generation.
// The result is handed back to the parent so "Add to board" reuses it rather than paying for
// the same picture twice.
const ImageComponent = ({ index, component, subject, resolved, onResolved, className }) => {
    const [src, setSrc] = useState(resolved || null);
    const [imageError, setImageError] = useState(false);
    const requestedRef = useRef(false);

    useEffect(() => {
        if (resolved) {
            setSrc(resolved);
            return undefined;
        }
        // Guard against a re-render firing a second lookup for the same component.
        if (requestedRef.current || !component.content) {
            return undefined;
        }
        requestedRef.current = true;

        let cancelled = false;
        resolveIllustration(component, { subject })
            .then((res) => {
                if (cancelled) return;
                if (!res || !res.dataUrl) {
                    setImageError(true);
                    return;
                }
                setSrc(res.dataUrl);
                if (onResolved) {
                    onResolved(index, res);
                }
            })
            .catch(() => {
                // A failed picture must never take the rest of the lesson down with it.
                if (!cancelled) setImageError(true);
            });

        return () => {
            cancelled = true;
        };
        // onResolved is redefined every parent render and must not retrigger the lookup.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [component, subject, resolved]);

    if (imageError) {
        return (
            <Box className={className}>
                <Typography color='error' variant='body2' align='center'>
                    <FormattedMessage id='ai.dialog.imageFailed' />
                </Typography>
            </Box>
        );
    }

    if (!src) {
        return (
            <Box className={className}>
                <Typography variant='body2' color='textSecondary' align='center'>
                    <FormattedMessage id='ai.dialog.generatingImage' />
                </Typography>
            </Box>
        );
    }

    return (
        <Box className={className}>
            <img
                src={src}
                alt={component.content}
                onError={() => setImageError(true)}
                style={{
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                }}
            />
        </Box>
    );
};

const BlockLatexComponent = ({ content }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (containerRef.current && window.latexToImg) {
            try {
                const svgElement = window.latexToImg(content);
                if (svgElement) {
                    svgElement.style.display = 'block';
                    svgElement.style.margin = '8px auto';
                    containerRef.current.innerHTML = '';
                    containerRef.current.appendChild(svgElement);
                }
            } catch (error) {
                console.error('Error rendering block LaTeX:', error);
                containerRef.current.textContent = `$$${content}$$`;
            }
        }
    }, [content]);

    return <div ref={containerRef} style={{ display: 'block', textAlign: 'center' }} />;
};

const MixedContentText = ({ content, className }) => {
    const parseContent = (text) => {
        const parts = [];
        let currentIndex = 0;

        // Regex to match display math $$...$$, inline math \(...\) or \\(...\\) or $...$, and bold **...**
        const regex = /(\$\$[^$]*\$\$|\\\\?\([^)]*\\\\?\)|\$[^$]*\$|\*\*[^*]+\*\*)/g;
        let match;

        while ((match = regex.exec(text)) !== null) {
            // Add text before the match
            if (match.index > currentIndex) {
                parts.push({
                    type: 'text',
                    content: text.substring(currentIndex, match.index),
                });
            }

            const matchedText = match[0];
            if (matchedText.startsWith('$$') && matchedText.endsWith('$$')) {
                // Display math with $$...$$ delimiters (block-level)
                parts.push({
                    type: 'displayMath',
                    content: matchedText.slice(2, -2),
                });
            } else if ((matchedText.startsWith('\\(') && matchedText.endsWith('\\)')) ||
                (matchedText.startsWith('\\\\(') && matchedText.endsWith('\\\\)'))) {
                // Inline LaTeX with \(...\) or \\(...\\) delimiters
                const sliceAmount = matchedText.startsWith('\\\\(') ? 3 : 2;
                const innerContent = matchedText.slice(sliceAmount, -sliceAmount);

                // Validate that content looks like LaTeX (has backslash, math symbols, or multiple chars)
                const looksLikeLaTeX = /[\\=+\-*/^_{}]/.test(innerContent) || innerContent.length > 3;

                if (looksLikeLaTeX) {
                    parts.push({
                        type: 'latex',
                        content: innerContent,
                    });
                } else {
                    // Not LaTeX, treat as plain text
                    parts.push({
                        type: 'text',
                        content: matchedText,
                    });
                }
            } else if (matchedText.startsWith('$') && matchedText.endsWith('$')) {
                // Inline LaTeX with $...$ delimiters
                parts.push({
                    type: 'latex',
                    content: matchedText.slice(1, -1),
                });
            } else if (matchedText.startsWith('**') && matchedText.endsWith('**')) {
                // Bold text
                parts.push({
                    type: 'bold',
                    content: matchedText.slice(2, -2),
                });
            }

            currentIndex = match.index + matchedText.length;
        }

        // Add remaining text
        if (currentIndex < text.length) {
            parts.push({
                type: 'text',
                content: text.substring(currentIndex),
            });
        }

        return parts;
    };

    const parts = parseContent(content);

    return (
        <Typography className={className} component='div'>
            {parts.map((part, index) => {
                if (part.type === 'displayMath') {
                    return <BlockLatexComponent key={index} content={part.content} />;
                } else if (part.type === 'latex') {
                    return <InlineLatexComponent key={index} content={part.content} />;
                } else if (part.type === 'bold') {
                    return <strong key={index}>{part.content}</strong>;
                } else {
                    return <span key={index}>{part.content}</span>;
                }
            })}
        </Typography>
    );
};

const AIResponseRenderer = ({ title, components, subject, resolvedImages, onImageResolved }) => {
    const classes = useStyles();

    const renderComponent = (component, index) => {
        switch (component.type) {
            case 'text':
                return (
                    <MixedContentText
                        key={index}
                        content={component.content}
                        className={`${classes.component} ${classes.textComponent}`}
                    />
                );

            case 'latex':
                // Check if content contains LaTeX list environments (itemize, enumerate)

                const hasListEnvironment = /\\begin\{(itemize|enumerate)\}/.test(component.content);

                if (hasListEnvironment) {
                    // Parse LaTeX list and render as HTML list with inline math
                    const items = component.content.match(/\\item\s+([^\n\\]+(?:\$[^$]*\$[^\n\\]*)*)/g) || [];
                    const listItems = items.map(item => item.replace(/\\item\s+/, '').trim());
                    const isOrdered = /\\begin\{enumerate\}/.test(component.content);

                    return (
                        <Box key={index} className={`${classes.component} ${classes.textComponent}`}>
                            {isOrdered ? (
                                <ol>
                                    {listItems.map((item, i) => (
                                        <li key={i}>
                                            <MixedContentText content={item} />
                                        </li>
                                    ))}
                                </ol>
                            ) : (
                                <ul>
                                    {listItems.map((item, i) => (
                                        <li key={i}>
                                            <MixedContentText content={item} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Box>
                    );
                }

                // Check if latex content contains delimiters (mixed content with text)
                const hasMixedContent = /[a-zA-Z\s]+\$/.test(component.content) || /\\\(.*?\\\)/.test(component.content);

                if (hasMixedContent) {
                    // Treat as mixed content text block (has text + inline formulas)
                    return (
                        <Box key={index} className={`${classes.component} ${classes.latexBlock}`}>
                            <MixedContentText
                                content={component.content}
                                className={classes.textComponent}
                            />
                        </Box>
                    );
                } else if (component.content.includes('\n')) {
                    // Multiple LaTeX formulas separated by newlines - render each on its own line
                    const formulas = component.content.split('\n').filter(line => line.trim());
                    return (
                        <Box key={index} className={`${classes.component} ${classes.latexBlock}`}>
                            {formulas.map((formula, i) => (
                                <Box key={i} style={{ maxHeight: '38px', overflow: 'visible' }}>
                                    <LatexComponent
                                        content={formula}
                                        className={classes.latexComponent}
                                    />
                                </Box>
                            ))}
                        </Box>
                    );
                } else {
                    // Single formula - render centered
                    return (
                        <LatexComponent
                            key={index}
                            content={component.content}
                            className={`${classes.component} ${classes.latexComponent}`}
                        />
                    );
                }

            case 'svg':
                return (
                    <Box key={index} className={`${classes.component} ${classes.svgComponent}`}
                         style={{ width: '100%', minHeight: 120 }}>
                        <div
                            style={{ width: '100%' }}
                            dangerouslySetInnerHTML={{ __html: component.content }}
                        />
                    </Box>
                );

            case 'image':
                return (
                    <ImageComponent
                        key={index}
                        index={index}
                        component={component}
                        subject={subject}
                        resolved={resolvedImages && resolvedImages[index] ? resolvedImages[index].dataUrl : null}
                        onResolved={onImageResolved}
                        className={`${classes.component} ${classes.imageComponent}`}
                    />
                );

            default:
                return (
                    <Typography key={index} className={classes.component} color='error'>
                        Unknown component type: {component.type}
                    </Typography>
                );
        }
    };

    return (
        <Box className={classes.container}>
            {title && (
                <Typography variant='h6' className={classes.title}>
                    {title}
                </Typography>
            )}
            {components && components.map((component, index) => renderComponent(component, index))}
        </Box>
    );
};

export default AIResponseRenderer;
